import { useEffect, useMemo, useState } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import AppLoader from "components/AppLoader";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";
import { formatDateForUser } from "utils/dateTime";

function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryImageDataUrl, setNewCategoryImageDataUrl] = useState("");
  const [newDisplayInCat, setNewDisplayInCat] = useState(false);
  const [newDisplayInMain, setNewDisplayInMain] = useState(false);
  const [newDisplayInSub, setNewDisplayInSub] = useState(false);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editCategoryId, setEditCategoryId] = useState(0);
  const [editCategoryImageDataUrl, setEditCategoryImageDataUrl] = useState("");
  const [editCategoryImageUrl, setEditCategoryImageUrl] = useState("");
  const [editDisplayInCat, setEditDisplayInCat] = useState(false);
  const [editDisplayInMain, setEditDisplayInMain] = useState(false);
  const [editDisplayInSub, setEditDisplayInSub] = useState(false);
  const [openListing, setOpenListing] = useState(false);
  const [listingCategoryId, setListingCategoryId] = useState(0);
  const [listingCategoryName, setListingCategoryName] = useState("");
  const [listingTitle, setListingTitle] = useState("");
  const [listingDescription, setListingDescription] = useState("");
  const [listingPrice, setListingPrice] = useState("");
  const [listingQuantity, setListingQuantity] = useState("");
  const [listingImageDataUrls, setListingImageDataUrls] = useState([]);
  const [openManageListings, setOpenManageListings] = useState(false);
  const [manageCategoryId, setManageCategoryId] = useState(0);
  const [manageCategoryName, setManageCategoryName] = useState("");
  const [activeListings, setActiveListings] = useState([]);
  const [inactiveListings, setInactiveListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [listingStatusLoadingId, setListingStatusLoadingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [openStatusConfirm, setOpenStatusConfirm] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [openMoveListing, setOpenMoveListing] = useState(false);
  const [moveListingItem, setMoveListingItem] = useState(null);
  const [moveTargetCategoryId, setMoveTargetCategoryId] = useState(0);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.$values)) return value.$values;
    return [];
  };
  const buildReferenceMap = (value, map = new Map(), visited = new WeakSet()) => {
    if (!value || typeof value !== "object") return map;
    if (visited.has(value)) return map;
    visited.add(value);

    if (typeof value.$id === "string" || typeof value.$id === "number") {
      map.set(String(value.$id), value);
    }

    if (Array.isArray(value)) {
      value.forEach((entry) => buildReferenceMap(entry, map, visited));
      return map;
    }

    Object.values(value).forEach((entry) => buildReferenceMap(entry, map, visited));
    return map;
  };
  const resolveReferenceObject = (value, referenceMap) => {
    if (!value || typeof value !== "object") return value;
    if (Array.isArray(value))
      return value.map((entry) => resolveReferenceObject(entry, referenceMap));
    if (value.$values && Array.isArray(value.$values)) {
      return value.$values.map((entry) => resolveReferenceObject(entry, referenceMap));
    }
    if (value.$ref !== undefined && value.$ref !== null) {
      return referenceMap.get(String(value.$ref)) ?? value;
    }
    return value;
  };
  const resolveReferencePreservedArray = (payload) => {
    const referenceMap = buildReferenceMap(payload);
    const items = normalizeArray(payload);
    return items.map((item) => resolveReferenceObject(item, referenceMap));
  };

  const parseBool = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1" || normalized === "active";
    }
    return false;
  };
  const parseBoolOrUndefined = (value) => {
    if (value === undefined || value === null || value === "") return undefined;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["true", "1", "active", "enabled", "yes", "y"].includes(normalized)) return true;
      if (["false", "0", "inactive", "disabled", "no", "n"].includes(normalized)) return false;
    }
    return undefined;
  };

  const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);
  const getId = (item) =>
    Number(
      firstDefined(
        item?.id,
        item?.Id,
        item?.categoryId,
        item?.CategoryId,
        item?.categoryID,
        item?.CategoryID
      )
    ) || 0;
  const getName = (item) =>
    String(
      firstDefined(item?.categoryName, item?.CategoryName, item?.name, item?.Name) ?? ""
    ).trim();
  const getCategoryImageUrl = (item) =>
    String(
      firstDefined(item?.imageUrl, item?.ImageUrl, item?.image, item?.Image, item?.thumbnail) ?? ""
    ).trim();
  const resolveImageUrl = (url) => {
    if (!url) return "";
    const trimmed = String(url).trim();
    if (!trimmed) return "";
    if (
      trimmed.startsWith("data:image/") ||
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://")
    ) {
      return trimmed;
    }
    if (trimmed.startsWith("/")) {
      return `${appSettings.baseUrl}${trimmed}`;
    }
    return trimmed;
  };
  const getCreatedDate = (item) =>
    firstDefined(
      item?.dateCreated,
      item?.DateCreated,
      item?.createdDate,
      item?.CreatedDate,
      item?.createdOn,
      item?.CreatedOn
    ) ?? "";
  const isActive = (item) =>
    parseBool(
      firstDefined(
        item?.isActive,
        item?.IsActive,
        item?.active,
        item?.Active,
        item?.status,
        item?.Status
      )
    );
  const getListingEntity = (item) =>
    firstDefined(
      item?.listing,
      item?.Listing,
      item?.item,
      item?.Item,
      item?.listingDto,
      item?.ListingDto,
      item
    );
  const pickFirstNonEmptyString = (...values) => {
    for (const value of values) {
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    return "";
  };
  const deepFindByKeyHint = (obj, hints, type, depth = 0, visited = new Set()) => {
    if (!obj || typeof obj !== "object" || depth > 4 || visited.has(obj)) return undefined;
    visited.add(obj);

    for (const [key, value] of Object.entries(obj)) {
      const lower = key.toLowerCase();
      const matched = hints.some((hint) => lower.includes(hint));
      if (matched && type === "string" && typeof value === "string" && value.trim()) {
        return value.trim();
      }
      if (matched && type === "number") {
        const asNumber = Number(value);
        if (Number.isFinite(asNumber) && asNumber > 0) return asNumber;
      }
    }

    for (const value of Object.values(obj)) {
      if (value && typeof value === "object") {
        const nested = deepFindByKeyHint(value, hints, type, depth + 1, visited);
        if (nested !== undefined) return nested;
      }
    }

    return undefined;
  };
  const findStringByKeyHint = (obj, hints) => {
    if (!obj || typeof obj !== "object") return "";
    const entries = Object.entries(obj);

    for (const [key, value] of entries) {
      if (typeof value !== "string" || !value.trim()) continue;
      const lower = key.toLowerCase();
      if (hints.some((hint) => lower.includes(hint))) return value.trim();
    }

    return "";
  };
  const getListingId = (item) =>
    Number(
      firstDefined(
        Number(
          firstDefined(
            getListingEntity(item)?.id,
            getListingEntity(item)?.Id,
            getListingEntity(item)?.listingId,
            getListingEntity(item)?.ListingId,
            getListingEntity(item)?.listingID,
            getListingEntity(item)?.ListingID
          )
        ),
        deepFindByKeyHint(
          getListingEntity(item),
          ["listingid", "listing_id", "listing-id", "id"],
          "number"
        )
      )
    ) || 0;
  const getListingTitle = (item) =>
    (() => {
      const listing = getListingEntity(item);
      const direct = pickFirstNonEmptyString(
        listing?.title,
        listing?.Title,
        listing?.listingTitle,
        listing?.ListingTitle,
        listing?.listingName,
        listing?.ListingName,
        listing?.name,
        listing?.Name,
        listing?.displayName,
        listing?.DisplayName,
        listing?.label,
        listing?.Label,
        listing?.itemName,
        listing?.ItemName,
        listing?.productName,
        listing?.ProductName
      );
      if (direct) return direct;
      return (
        findStringByKeyHint(listing, ["title", "name", "label"]) ||
        deepFindByKeyHint(listing, ["title", "name", "label"], "string") ||
        ""
      );
    })();
  const getListingStatusValue = (item) =>
    firstDefined(
      getListingEntity(item)?.isActive,
      getListingEntity(item)?.IsActive,
      getListingEntity(item)?.active,
      getListingEntity(item)?.Active,
      getListingEntity(item)?.status,
      getListingEntity(item)?.Status,
      getListingEntity(item)?.listingStatus,
      getListingEntity(item)?.ListingStatus
    );
  const isListingActive = (item, sourceHint) => {
    const parsed = parseBoolOrUndefined(getListingStatusValue(item));
    if (parsed !== undefined) return parsed;
    if (sourceHint !== undefined) return sourceHint;
    // Missing status from payload should not force listing into inactive.
    return true;
  };
  const getListingTitleLabel = (item) => {
    const title = getListingTitle(item);
    if (title) return title;
    const id = getListingId(item);
    return id ? `Listing #${id}` : "Untitled Listing";
  };
  const hydrateMissingListingTitles = async (activeList, inactiveList) => {
    const allCurrent = [...activeList, ...inactiveList];
    const hasMissingTitle = allCurrent.some(
      (item) => getListingId(item) > 0 && !getListingTitle(item)
    );
    if (!hasMissingTitle) {
      return { active: activeList, inactive: inactiveList };
    }

    try {
      const endpoint = appSettings?.endpoints?.getAllListings;
      if (!endpoint) {
        return { active: activeList, inactive: inactiveList };
      }

      const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
      const response = await fetchWithAuth(url, { method: "GET", cache: "no-store" });
      if (!response.ok) {
        return { active: activeList, inactive: inactiveList };
      }

      const payload = await response.json();
      const allListings = normalizeArray(
        firstDefined(payload?.listings, payload?.Listings, payload?.items, payload)
      ).map((item) => getListingEntity(item));

      const titleById = new Map();
      allListings.forEach((item) => {
        const id = getListingId(item);
        const title = getListingTitle(item);
        if (id > 0 && title) {
          titleById.set(String(id), title);
        }
      });

      const applyTitleFallback = (list) =>
        list.map((item) => {
          if (getListingTitle(item)) return item;
          const id = getListingId(item);
          if (!id) return item;
          const title = titleById.get(String(id));
          if (!title) return item;
          return {
            ...item,
            title,
            Title: title,
            listingTitle: title,
            ListingTitle: title,
          };
        });

      return {
        active: applyTitleFallback(activeList),
        inactive: applyTitleFallback(inactiveList),
      };
    } catch {
      return { active: activeList, inactive: inactiveList };
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.getAllCategories;
      if (!endpoint) {
        throw new Error("Categories endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
      const response = await fetchWithAuth(url, { method: "GET", cache: "no-store" });

      if (!response.ok) {
        throw new Error("Failed to load categories.");
      }

      const data = await response.json();
      setCategories(normalizeArray(data));
    } catch (e) {
      setError(e.message || "Failed to load categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleOpenCreate = () => {
    setNewCategoryName("");
    setNewCategoryImageDataUrl("");
    setNewDisplayInCat(false);
    setNewDisplayInMain(false);
    setNewDisplayInSub(false);
    setOpenCreate(true);
  };

  const handleCloseCreate = () => {
    if (saving) return;
    setOpenCreate(false);
    setNewCategoryName("");
    setNewCategoryImageDataUrl("");
    setNewDisplayInCat(false);
    setNewDisplayInMain(false);
    setNewDisplayInSub(false);
  };

  const handleOpenEdit = (category) => {
    const categoryId = getId(category);
    setEditCategoryId(categoryId);
    setEditCategoryName(getName(category));
    setEditCategoryImageDataUrl("");
    setEditCategoryImageUrl(getCategoryImageUrl(category));
    setEditDisplayInCat(
      parseBool(
        firstDefined(category?.displayInCat, category?.DisplayInCat, category?.displayInCAT)
      )
    );
    setEditDisplayInMain(
      parseBool(
        firstDefined(category?.displayInMain, category?.DisplayInMain, category?.displayInMAIN)
      )
    );
    setEditDisplayInSub(
      parseBool(
        firstDefined(category?.displayInSub, category?.DisplayInSub, category?.displayInSUB)
      )
    );
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    if (saving) return;
    setOpenEdit(false);
    setEditCategoryId(0);
    setEditCategoryName("");
    setEditCategoryImageDataUrl("");
    setEditCategoryImageUrl("");
    setEditDisplayInCat(false);
    setEditDisplayInMain(false);
    setEditDisplayInSub(false);
  };

  const handleOpenListing = (category) => {
    const categoryId = getId(category);
    if (!categoryId) {
      setError("Category id is missing.");
      return;
    }

    setListingCategoryId(categoryId);
    setListingCategoryName(getName(category));
    setListingTitle("");
    setListingDescription("");
    setListingPrice("");
    setListingQuantity("");
    setListingImageDataUrls([]);
    setOpenListing(true);
  };

  const handleCloseListing = () => {
    if (saving) return;
    setOpenListing(false);
    setListingCategoryId(0);
    setListingCategoryName("");
    setListingTitle("");
    setListingDescription("");
    setListingPrice("");
    setListingQuantity("");
    setListingImageDataUrls([]);
  };

  const convertFileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
      reader.onerror = () => reject(new Error(`Failed to read image file: ${file?.name || ""}`));
      reader.readAsDataURL(file);
    });

  const handleListingImagesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    try {
      const firstFile = files[0];
      const fileName = String(firstFile?.name || "").toLowerCase();
      const mimeType = String(firstFile?.type || "").toLowerCase();
      const isPngOrJpg =
        mimeType === "image/png" ||
        mimeType === "image/jpeg" ||
        fileName.endsWith(".png") ||
        fileName.endsWith(".jpg") ||
        fileName.endsWith(".jpeg");

      if (!isPngOrJpg) {
        throw new Error("Only PNG and JPG images are allowed.");
      }

      const firstDataUrl = await convertFileToDataUrl(files[0]);
      if (!firstDataUrl) return;
      // Listing image upload supports exactly one image.
      setListingImageDataUrls([firstDataUrl]);
    } catch (e) {
      setError(e.message || "Failed to read selected image.");
    } finally {
      // allow selecting the same file again if needed
      // eslint-disable-next-line no-param-reassign
      event.target.value = "";
    }
  };

  const handleRemoveSelectedImage = (indexToRemove) => {
    setListingImageDataUrls((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleCategoryImageSelected = async (event, mode) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    try {
      const firstFile = files[0];
      const fileName = String(firstFile?.name || "").toLowerCase();
      const mimeType = String(firstFile?.type || "").toLowerCase();
      const isPngOrJpg =
        mimeType === "image/png" ||
        mimeType === "image/jpeg" ||
        fileName.endsWith(".png") ||
        fileName.endsWith(".jpg") ||
        fileName.endsWith(".jpeg");

      if (!isPngOrJpg) {
        throw new Error("Only PNG and JPG images are allowed.");
      }

      const firstDataUrl = await convertFileToDataUrl(files[0]);
      if (!firstDataUrl) return;

      if (mode === "edit") {
        setEditCategoryImageDataUrl(firstDataUrl);
      } else {
        setNewCategoryImageDataUrl(firstDataUrl);
      }
    } catch (e) {
      setError(e.message || "Failed to read selected category image.");
    } finally {
      // eslint-disable-next-line no-param-reassign
      event.target.value = "";
    }
  };

  const fetchCategoryListings = async (categoryId) => {
    if (!categoryId) return;

    setListingsLoading(true);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.getListingsByCategory;
      if (!endpoint) {
        throw new Error("Get listings by category endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}/${categoryId}?_ts=${Date.now()}`;
      const response = await fetchWithAuth(url, {
        method: "GET",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("Failed to load listings.");
      }

      const data = await response.json();
      const activeFromPayload = resolveReferencePreservedArray(
        firstDefined(data?.activeListings, data?.ActiveListings, data?.active, data?.Active)
      );
      const inactiveFromPayload = resolveReferencePreservedArray(
        firstDefined(data?.inactiveListings, data?.InactiveListings, data?.inactive, data?.Inactive)
      );

      if (activeFromPayload.length > 0 || inactiveFromPayload.length > 0) {
        const normalizedActive = activeFromPayload.map((item) => getListingEntity(item));
        const normalizedInactive = inactiveFromPayload.map((item) => getListingEntity(item));
        const hydrated = await hydrateMissingListingTitles(normalizedActive, normalizedInactive);
        setActiveListings(hydrated.active);
        setInactiveListings(hydrated.inactive);
        return;
      }

      const listings = resolveReferencePreservedArray(
        firstDefined(data?.listings, data?.Listings, data?.items, data)
      );
      const normalizedListings = listings.map((item) => getListingEntity(item));
      const active = normalizedListings.filter((item) => isListingActive(item));
      const inactive = normalizedListings.filter((item) => !isListingActive(item));
      const hydrated = await hydrateMissingListingTitles(active, inactive);
      setActiveListings(hydrated.active);
      setInactiveListings(hydrated.inactive);
    } catch (e) {
      setError(e.message || "Failed to load listings.");
      setActiveListings([]);
      setInactiveListings([]);
    } finally {
      setListingsLoading(false);
    }
  };

  const handleOpenManageListings = async (category) => {
    const categoryId = getId(category);
    if (!categoryId) {
      setError("Category id is missing.");
      return;
    }

    setManageCategoryId(categoryId);
    setManageCategoryName(getName(category));
    setOpenManageListings(true);
    await fetchCategoryListings(categoryId);
  };

  const handleCloseManageListings = () => {
    if (listingsLoading || listingStatusLoadingId !== null) return;
    setOpenManageListings(false);
    setManageCategoryId(0);
    setManageCategoryName("");
    setActiveListings([]);
    setInactiveListings([]);
  };

  const handleOpenMoveListing = (listing) => {
    const listingId = getListingId(listing);
    if (!listingId) {
      setError("Listing id is missing.");
      return;
    }

    setMoveListingItem(listing);
    setMoveTargetCategoryId(0);
    setOpenMoveListing(true);
  };

  const handleCloseMoveListing = () => {
    if (listingStatusLoadingId !== null) return;
    setOpenMoveListing(false);
    setMoveListingItem(null);
    setMoveTargetCategoryId(0);
  };

  const handleConfirmMoveListing = async () => {
    const listingId = getListingId(moveListingItem);
    if (!listingId) {
      setError("Listing id is missing.");
      return;
    }
    if (!moveTargetCategoryId) {
      setError("Please select a target category.");
      return;
    }
    if (Number(moveTargetCategoryId) === Number(manageCategoryId)) {
      setError("Please select a different category.");
      return;
    }

    setListingStatusLoadingId(listingId);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.updateListingCategory;
      if (!endpoint) {
        throw new Error("Update listing category endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          listingId,
          categoryId: Number(moveTargetCategoryId),
        }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to move listing to selected category.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors and keep default message.
        }
        throw new Error(errorMessage);
      }

      handleCloseMoveListing();
      await fetchCategoryListings(manageCategoryId);
    } catch (e) {
      setError(e.message || "Failed to move listing to selected category.");
    } finally {
      setListingStatusLoadingId(null);
    }
  };

  const handleMoveListing = async (listing, nextIsActive) => {
    const listingId = getListingId(listing);
    if (!listingId) {
      setError("Listing id is missing.");
      return;
    }

    setListingStatusLoadingId(listingId);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.updateListingStatus;
      if (!endpoint) {
        throw new Error("Update listing status endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({ listingId, isActive: nextIsActive }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to update listing status.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors and keep default message.
        }
        throw new Error(errorMessage);
      }

      const updatedListing = {
        ...listing,
        isActive: nextIsActive,
        IsActive: nextIsActive,
      };

      if (nextIsActive) {
        setInactiveListings((prev) => prev.filter((item) => getListingId(item) !== listingId));
        setActiveListings((prev) => [...prev, updatedListing]);
      } else {
        setActiveListings((prev) => prev.filter((item) => getListingId(item) !== listingId));
        setInactiveListings((prev) => [...prev, updatedListing]);
      }
    } catch (e) {
      setError(e.message || "Failed to update listing status.");
    } finally {
      setListingStatusLoadingId(null);
    }
  };

  const handleListingDragStart = (event, listing, source) => {
    event.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        source,
        listingId: getListingId(listing),
      })
    );
  };

  const handleDropOnActive = async (event) => {
    event.preventDefault();
    if (listingsLoading || listingStatusLoadingId !== null) return;

    let payload;
    try {
      payload = JSON.parse(event.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }

    if (!payload || payload.source !== "inactive") return;

    const listing = inactiveListings.find(
      (item) => String(getListingId(item)) === String(payload.listingId)
    );
    if (!listing) return;

    await handleMoveListing(listing, true);
  };

  const handleDropOnInactive = async (event) => {
    event.preventDefault();
    if (listingsLoading || listingStatusLoadingId !== null) return;

    let payload;
    try {
      payload = JSON.parse(event.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }

    if (!payload || payload.source !== "active") return;

    const listing = activeListings.find(
      (item) => String(getListingId(item)) === String(payload.listingId)
    );
    if (!listing) return;

    await handleMoveListing(listing, false);
  };

  const handleSaveCategory = async () => {
    const trimmedName = newCategoryName.trim();
    if (!trimmedName) {
      setError("Category name is required.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.createCategory;
      if (!endpoint) {
        throw new Error("Create category endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          name: trimmedName,
          imageDataUrl: newCategoryImageDataUrl || null,
          displayInCat: newDisplayInCat,
          displayInMain: newDisplayInMain,
          displayInSub: newDisplayInSub,
        }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to create category.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors and keep default message.
        }
        throw new Error(errorMessage);
      }

      handleCloseCreate();
      await fetchCategories();
    } catch (e) {
      setError(e.message || "Failed to create category.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEditCategory = async () => {
    const trimmedName = editCategoryName.trim();
    if (!editCategoryId) {
      setError("Category id is missing.");
      return;
    }
    if (!trimmedName) {
      setError("Category name is required.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.updateCategory;
      if (!endpoint) {
        throw new Error("Update category endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          categoryId: editCategoryId,
          name: trimmedName,
          imageDataUrl: editCategoryImageDataUrl || null,
          imageUrl: editCategoryImageDataUrl ? null : editCategoryImageUrl || null,
          displayInCat: editDisplayInCat,
          displayInMain: editDisplayInMain,
          displayInSub: editDisplayInSub,
        }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to update category.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors and keep default message.
        }
        throw new Error(errorMessage);
      }

      handleCloseEdit();
      await fetchCategories();
    } catch (e) {
      setError(e.message || "Failed to update category.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveListing = async () => {
    const trimmedTitle = listingTitle.trim();
    const trimmedDescription = listingDescription.trim();
    const parsedPrice = Number(listingPrice);
    const parsedQuantity = Number(listingQuantity);

    if (!listingCategoryId) {
      setError("Category id is missing.");
      return;
    }
    if (!trimmedTitle) {
      setError("Listing title is required.");
      return;
    }
    if (!trimmedDescription) {
      setError("Listing description is required.");
      return;
    }
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      setError("Listing price is required and must be a valid non-negative value.");
      return;
    }
    if (!Number.isInteger(parsedQuantity) || parsedQuantity < 0) {
      setError("A valid non-negative quantity is required.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.createListing;
      if (!endpoint) {
        throw new Error("Create listing endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          categoryId: listingCategoryId,
          title: trimmedTitle,
          description: trimmedDescription,
          price: parsedPrice,
          quantity: parsedQuantity,
          imageDataUrls: listingImageDataUrls,
        }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to create listing.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors and keep default message.
        }
        throw new Error(errorMessage);
      }

      await fetchCategoryListings(listingCategoryId);
      handleCloseListing();
    } catch (e) {
      setError(e.message || "Failed to create listing.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenStatusConfirm = (category) => {
    setSelectedCategory(category);
    setOpenStatusConfirm(true);
  };

  const handleCloseStatusConfirm = () => {
    if (actionLoadingId !== null) return;
    setOpenStatusConfirm(false);
    setSelectedCategory(null);
  };

  const handleOpenDeleteConfirm = (category) => {
    setSelectedCategory(category);
    setOpenDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    if (actionLoadingId !== null) return;
    setOpenDeleteConfirm(false);
    setSelectedCategory(null);
  };

  const handleToggleCategoryStatus = async () => {
    const categoryId = getId(selectedCategory);
    if (!categoryId) {
      setError("Category id is missing.");
      return;
    }

    setActionLoadingId(categoryId);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.toggleCategoryStatus;
      if (!endpoint) {
        throw new Error("Toggle category status endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({ categoryId }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to update category status.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors and keep default message.
        }
        throw new Error(errorMessage);
      }

      handleCloseStatusConfirm();
      await fetchCategories();
    } catch (e) {
      setError(e.message || "Failed to update category status.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteCategory = async () => {
    const categoryId = getId(selectedCategory);
    if (!categoryId) {
      setError("Category id is missing.");
      return;
    }

    setActionLoadingId(categoryId);
    setError(null);

    try {
      const endpoint = appSettings?.endpoints?.deleteCategory;
      if (!endpoint) {
        throw new Error("Delete category endpoint is not configured.");
      }

      const url = `${appSettings.baseUrl}${endpoint}`;
      const response = await fetchWithAuth(url, {
        method: "DELETE",
        body: JSON.stringify({ categoryId }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to delete category.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch {
          // Ignore JSON parse errors and keep default message.
        }
        throw new Error(errorMessage);
      }

      handleCloseDeleteConfirm();
      await fetchCategories();
    } catch (e) {
      setError(e.message || "Failed to delete category.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const tableData = useMemo(() => {
    const columns = [
      {
        Header: "category",
        accessor: "categorySearch",
        align: "left",
        Cell: ({ row }) => row.original.category,
      },
      {
        Header: "status",
        accessor: "statusText",
        align: "center",
        Cell: ({ row }) => row.original.status,
      },
      {
        Header: "created",
        accessor: "createdText",
        align: "left",
        Cell: ({ row }) => row.original.created,
      },
      {
        Header: "actions",
        accessor: "actionsText",
        align: "center",
        Cell: ({ row }) => row.original.actions,
      },
    ];

    const rows = categories.map((item) => {
      const name = getName(item);
      const statusText = isActive(item) ? "active" : "inactive";
      const createdText = formatDateForUser(getCreatedDate(item));
      const categoryId = getId(item);

      return {
        categorySearch: name,
        category: (
          <MDBox display="flex" alignItems="center" gap={1}>
            {getCategoryImageUrl(item) && (
              <MDBox
                component="img"
                src={resolveImageUrl(getCategoryImageUrl(item))}
                alt={name}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  objectFit: "cover",
                  border: "1px solid rgba(0,0,0,0.12)",
                }}
              />
            )}
            <MDTypography variant="button" color="text">
              {name}
            </MDTypography>
          </MDBox>
        ),
        statusText,
        status: (
          <MDBadge
            badgeContent={statusText}
            color={isActive(item) ? "info" : "secondary"}
            variant="gradient"
            size="sm"
          />
        ),
        createdText,
        created: (
          <MDTypography variant="caption" color="text">
            {createdText}
          </MDTypography>
        ),
        actionsText: `ID: ${categoryId || "-"}`,
        actions: (
          <MDBox display="flex" justifyContent="center" alignItems="center" gap={1}>
            <Tooltip title="Edit Category">
              <span>
                <IconButton
                  color="info"
                  size="small"
                  onClick={() => handleOpenEdit(item)}
                  disabled={saving || actionLoadingId === categoryId}
                >
                  <Icon fontSize="small">edit</Icon>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isActive(item) ? "Deactivate Category" : "Activate Category"}>
              <span>
                <IconButton
                  color={isActive(item) ? "warning" : "success"}
                  size="small"
                  onClick={() => handleOpenStatusConfirm(item)}
                  disabled={actionLoadingId === categoryId}
                >
                  <Icon fontSize="small">{isActive(item) ? "toggle_off" : "toggle_on"}</Icon>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Category">
              <span>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => handleOpenDeleteConfirm(item)}
                  disabled={actionLoadingId === categoryId}
                >
                  <Icon fontSize="small">delete</Icon>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Add Listing">
              <span>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => handleOpenListing(item)}
                  disabled={saving || actionLoadingId === categoryId}
                >
                  <Icon fontSize="small">post_add</Icon>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Manage Listings">
              <span>
                <IconButton
                  color="dark"
                  size="small"
                  onClick={() => handleOpenManageListings(item)}
                  disabled={saving || actionLoadingId === categoryId}
                >
                  <Icon fontSize="small">view_column</Icon>
                </IconButton>
              </span>
            </Tooltip>
          </MDBox>
        ),
      };
    });

    return { columns, rows };
  }, [categories, actionLoadingId]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Categories
                </MDTypography>
                <MDBox display="flex" gap={1}>
                  <MDButton variant="gradient" color="success" onClick={handleOpenCreate}>
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;add category
                  </MDButton>
                  <MDButton variant="gradient" color="success" onClick={fetchCategories}>
                    <Icon sx={{ fontWeight: "bold" }}>refresh</Icon>
                    &nbsp;refresh
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={10}>
                    <AppLoader size={42} />
                  </MDBox>
                ) : (
                  <>
                    {error && (
                      <MDBox
                        mx={2}
                        mb={2}
                        py={1}
                        px={2}
                        variant="gradient"
                        sx={{ backgroundColor: "lightcoral" }}
                        borderRadius="lg"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDTypography variant="button" color="white" fontWeight="medium">
                          {error}
                        </MDTypography>
                        <MDTypography
                          color="white"
                          sx={{ cursor: "pointer", ml: 2, lineHeight: 0 }}
                          onClick={() => setError(null)}
                        >
                          x
                        </MDTypography>
                      </MDBox>
                    )}

                    <DataTable
                      table={tableData}
                      isSorted
                      canSearch
                      entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50] }}
                      showTotalEntries
                      noEndBorder
                    />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={openCreate} onClose={handleCloseCreate} fullWidth maxWidth="sm">
        <DialogTitle>Add Category</DialogTitle>
        <DialogContent>
          <MDBox mt={1} display="grid" gap={2}>
            <TextField
              autoFocus
              fullWidth
              label="Category Name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !saving) {
                  handleSaveCategory();
                }
              }}
            />
            <MDBox>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Category Image
              </MDTypography>
              <MDBox
                px={1.25}
                py={1.25}
                borderRadius="md"
                sx={{ border: "1px dashed rgba(0,0,0,0.24)", backgroundColor: "rgba(0,0,0,0.01)" }}
              >
                <input
                  id="category-image-upload-create"
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={(e) => handleCategoryImageSelected(e, "create")}
                  style={{ display: "none" }}
                />
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  flexWrap="wrap"
                >
                  <MDButton
                    component="label"
                    htmlFor="category-image-upload-create"
                    variant="outlined"
                    color="info"
                    size="small"
                  >
                    {newCategoryImageDataUrl ? "Replace Image" : "Upload Image"}
                  </MDButton>
                  <MDTypography variant="caption" color="text">
                    {newCategoryImageDataUrl
                      ? "1 image selected"
                      : "PNG or JPG only. Only one image."}
                  </MDTypography>
                </MDBox>
              </MDBox>
              {newCategoryImageDataUrl && (
                <MDBox
                  mt={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  px={1}
                  py={0.75}
                  borderRadius="md"
                  sx={{ backgroundColor: "rgba(0,0,0,0.04)" }}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDBox
                      component="img"
                      src={newCategoryImageDataUrl}
                      alt="new-category-preview"
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "6px",
                        objectFit: "cover",
                      }}
                    />
                    <MDTypography variant="caption" color="text">
                      Category image selected
                    </MDTypography>
                  </MDBox>
                  <MDButton
                    size="small"
                    color="error"
                    variant="text"
                    onClick={() => setNewCategoryImageDataUrl("")}
                  >
                    Remove
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
            <MDBox display="grid" gap={0.4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newDisplayInCat}
                    onChange={(e) => setNewDisplayInCat(e.target.checked)}
                  />
                }
                label="Display In category section"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newDisplayInMain}
                    onChange={(e) => setNewDisplayInMain(e.target.checked)}
                  />
                }
                label="Display in Main banners"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newDisplayInSub}
                    onChange={(e) => setNewDisplayInSub(e.target.checked)}
                  />
                }
                label="Display in Sub Banners"
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={handleCloseCreate} color="secondary" disabled={saving}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveCategory}
            variant="gradient"
            color="success"
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <MDBox mt={1} display="grid" gap={2}>
            <TextField
              autoFocus
              fullWidth
              label="Category Name"
              value={editCategoryName}
              onChange={(e) => setEditCategoryName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !saving) {
                  handleSaveEditCategory();
                }
              }}
            />
            <MDBox>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Category Image
              </MDTypography>
              <MDBox
                px={1.25}
                py={1.25}
                borderRadius="md"
                sx={{ border: "1px dashed rgba(0,0,0,0.24)", backgroundColor: "rgba(0,0,0,0.01)" }}
              >
                <input
                  id="category-image-upload-edit"
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={(e) => handleCategoryImageSelected(e, "edit")}
                  style={{ display: "none" }}
                />
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  flexWrap="wrap"
                >
                  <MDButton
                    component="label"
                    htmlFor="category-image-upload-edit"
                    variant="outlined"
                    color="info"
                    size="small"
                  >
                    {editCategoryImageDataUrl || editCategoryImageUrl
                      ? "Replace Image"
                      : "Upload Image"}
                  </MDButton>
                  <MDTypography variant="caption" color="text">
                    {editCategoryImageDataUrl || editCategoryImageUrl
                      ? "1 image selected"
                      : "PNG or JPG only. Only one image."}
                  </MDTypography>
                </MDBox>
              </MDBox>
              {(editCategoryImageDataUrl || editCategoryImageUrl) && (
                <MDBox
                  mt={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  px={1}
                  py={0.75}
                  borderRadius="md"
                  sx={{ backgroundColor: "rgba(0,0,0,0.04)" }}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDBox
                      component="img"
                      src={resolveImageUrl(editCategoryImageDataUrl || editCategoryImageUrl)}
                      alt="edit-category-preview"
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "6px",
                        objectFit: "cover",
                      }}
                    />
                    <MDTypography variant="caption" color="text">
                      Category image selected
                    </MDTypography>
                  </MDBox>
                  <MDButton
                    size="small"
                    color="error"
                    variant="text"
                    onClick={() => {
                      setEditCategoryImageDataUrl("");
                      setEditCategoryImageUrl("");
                    }}
                  >
                    Remove
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
            <MDBox display="grid" gap={0.4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editDisplayInCat}
                    onChange={(e) => setEditDisplayInCat(e.target.checked)}
                  />
                }
                label="Display In category section"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editDisplayInMain}
                    onChange={(e) => setEditDisplayInMain(e.target.checked)}
                  />
                }
                label="Display in Main banners"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editDisplayInSub}
                    onChange={(e) => setEditDisplayInSub(e.target.checked)}
                  />
                }
                label="Display in Sub Banners"
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={handleCloseEdit} color="secondary" disabled={saving}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveEditCategory}
            variant="gradient"
            color="info"
            disabled={saving}
          >
            {saving ? "Saving..." : "Update"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openListing} onClose={handleCloseListing} fullWidth maxWidth="sm">
        <DialogTitle>
          Add Listing {listingCategoryName ? `- ${listingCategoryName}` : ""}
        </DialogTitle>
        <DialogContent>
          <MDBox mt={1} display="grid" gap={2}>
            <TextField
              autoFocus
              fullWidth
              label="Title"
              required
              value={listingTitle}
              onChange={(e) => setListingTitle(e.target.value)}
            />
            <TextField
              fullWidth
              label="Description"
              required
              value={listingDescription}
              onChange={(e) => setListingDescription(e.target.value)}
              multiline
              minRows={3}
            />
            <TextField
              fullWidth
              label="Price"
              type="number"
              required
              inputProps={{ min: 0, step: "0.01" }}
              value={listingPrice}
              onChange={(e) => setListingPrice(e.target.value)}
            />
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              inputProps={{ min: 0, step: "1" }}
              value={listingQuantity}
              onChange={(e) => setListingQuantity(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !saving) {
                  handleSaveListing();
                }
              }}
            />
            <MDBox>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Listing Image *
              </MDTypography>
              <MDBox
                px={1.25}
                py={1.25}
                borderRadius="md"
                sx={{ border: "1px dashed rgba(0,0,0,0.24)", backgroundColor: "rgba(0,0,0,0.01)" }}
              >
                <input
                  id="listing-image-upload-input"
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleListingImagesSelected}
                  style={{ display: "none" }}
                />
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  flexWrap="wrap"
                >
                  <MDButton
                    component="label"
                    htmlFor="listing-image-upload-input"
                    variant="outlined"
                    color="info"
                    size="small"
                  >
                    {listingImageDataUrls.length > 0 ? "Replace Image" : "Upload Image"}
                  </MDButton>
                  <MDTypography variant="caption" color="text">
                    {listingImageDataUrls.length > 0
                      ? "1 image selected"
                      : "PNG or JPG only. Only one image is allowed."}
                  </MDTypography>
                </MDBox>
              </MDBox>
              {listingImageDataUrls.length > 0 && (
                <MDBox mt={1} display="grid" gap={1}>
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    px={1}
                    py={0.75}
                    borderRadius="md"
                    sx={{ backgroundColor: "rgba(0,0,0,0.04)" }}
                  >
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDBox
                        component="img"
                        src={listingImageDataUrls[0]}
                        alt="listing-upload-preview"
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "6px",
                          objectFit: "cover",
                        }}
                      />
                      <MDTypography variant="caption" color="text">
                        Listing image selected
                      </MDTypography>
                    </MDBox>
                    <MDButton
                      size="small"
                      color="error"
                      variant="text"
                      onClick={() => handleRemoveSelectedImage(0)}
                    >
                      Remove
                    </MDButton>
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={handleCloseListing} color="secondary" disabled={saving}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveListing}
            variant="gradient"
            color="primary"
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Listing"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openManageListings} onClose={handleCloseManageListings} fullWidth maxWidth="md">
        <DialogTitle>Listings {manageCategoryName ? `- ${manageCategoryName}` : ""}</DialogTitle>
        <DialogContent>
          {listingsLoading ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" py={5}>
              <AppLoader size={42} />
            </MDBox>
          ) : (
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12} md={6}>
                <Card>
                  <MDBox px={2} py={1.5} bgColor="secondary" borderRadius="lg">
                    <MDTypography variant="button" color="white" fontWeight="bold">
                      Inactive Listings ({inactiveListings.length})
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    p={2}
                    display="flex"
                    flexDirection="column"
                    gap={1.25}
                    sx={{
                      maxHeight: 420,
                      minHeight: 240,
                      overflowY: "auto",
                      backgroundColor: "#f5f5f5",
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDropOnInactive}
                  >
                    {inactiveListings.length === 0 && (
                      <MDTypography variant="caption" color="text">
                        No inactive listings.
                      </MDTypography>
                    )}
                    {inactiveListings.map((item, index) => {
                      const id = getListingId(item);
                      return (
                        <MDBox
                          key={`inactive-${id || "na"}-${index}`}
                          p={1}
                          border="1px solid"
                          borderColor="secondary"
                          borderRadius="md"
                          draggable={listingStatusLoadingId === null}
                          onDragStart={(e) => handleListingDragStart(e, item, "inactive")}
                          sx={{
                            cursor: listingStatusLoadingId !== null ? "not-allowed" : "grab",
                            transition: "all 0.15s ease-in-out",
                            "&:hover": {
                              backgroundColor: "#eceff1",
                            },
                          }}
                        >
                          <MDTypography variant="caption" fontWeight="medium">
                            {getListingTitleLabel(item)}
                          </MDTypography>
                          <MDBox display="flex" justifyContent="flex-end" mt={0.5}>
                            <Tooltip title="Move To Another Category">
                              <span>
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleOpenMoveListing(item)}
                                  disabled={listingStatusLoadingId !== null}
                                >
                                  <Icon fontSize="small">drive_file_move</Icon>
                                </IconButton>
                              </span>
                            </Tooltip>
                          </MDBox>
                        </MDBox>
                      );
                    })}
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <MDBox px={2} py={1.5} bgColor="success" borderRadius="lg">
                    <MDTypography variant="button" color="white" fontWeight="bold">
                      Active Listings ({activeListings.length})
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    p={2}
                    display="flex"
                    flexDirection="column"
                    gap={1.25}
                    sx={{
                      maxHeight: 420,
                      minHeight: 240,
                      overflowY: "auto",
                      backgroundColor: "rgba(76, 175, 80, 0.08)",
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDropOnActive}
                  >
                    {activeListings.length === 0 && (
                      <MDTypography variant="caption" color="text">
                        No active listings.
                      </MDTypography>
                    )}
                    {activeListings.map((item, index) => {
                      const id = getListingId(item);
                      return (
                        <MDBox
                          key={`active-${id || "na"}-${index}`}
                          p={1}
                          border="1px solid"
                          borderColor="success"
                          borderRadius="md"
                          draggable={listingStatusLoadingId === null}
                          onDragStart={(e) => handleListingDragStart(e, item, "active")}
                          sx={{
                            backgroundColor: "rgba(76, 175, 80, 0.16)",
                            cursor: listingStatusLoadingId !== null ? "not-allowed" : "grab",
                            transition: "all 0.15s ease-in-out",
                            "&:hover": {
                              backgroundColor: "rgba(76, 175, 80, 0.24)",
                            },
                          }}
                        >
                          <MDTypography variant="caption" fontWeight="medium">
                            {getListingTitleLabel(item)}
                          </MDTypography>
                          <MDBox display="flex" justifyContent="flex-end" mt={0.5}>
                            <Tooltip title="Move To Another Category">
                              <span>
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleOpenMoveListing(item)}
                                  disabled={listingStatusLoadingId !== null}
                                >
                                  <Icon fontSize="small">drive_file_move</Icon>
                                </IconButton>
                              </span>
                            </Tooltip>
                          </MDBox>
                        </MDBox>
                      );
                    })}
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={() => fetchCategoryListings(manageCategoryId)}
            color="info"
            variant="outlined"
            disabled={listingsLoading || listingStatusLoadingId !== null}
          >
            Refresh
          </MDButton>
          <MDButton
            onClick={handleCloseManageListings}
            color="secondary"
            disabled={listingsLoading || listingStatusLoadingId !== null}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openStatusConfirm} onClose={handleCloseStatusConfirm} fullWidth maxWidth="xs">
        <DialogTitle>
          {isActive(selectedCategory) ? "Deactivate Category" : "Activate Category"}
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to {isActive(selectedCategory) ? "deactivate" : "activate"}{" "}
            <strong>{getName(selectedCategory) || "this category"}</strong>?
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseStatusConfirm}
            color="secondary"
            disabled={actionLoadingId !== null}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleToggleCategoryStatus}
            variant="gradient"
            color={isActive(selectedCategory) ? "warning" : "success"}
            disabled={actionLoadingId !== null}
          >
            {isActive(selectedCategory) ? "Deactivate" : "Activate"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteConfirm} onClose={handleCloseDeleteConfirm} fullWidth maxWidth="xs">
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Delete <strong>{getName(selectedCategory) || "this category"}</strong>? This action
            cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseDeleteConfirm}
            color="secondary"
            disabled={actionLoadingId !== null}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleDeleteCategory}
            variant="gradient"
            color="error"
            disabled={actionLoadingId !== null}
          >
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openMoveListing} onClose={handleCloseMoveListing} fullWidth maxWidth="xs">
        <DialogTitle>Move Listing</DialogTitle>
        <DialogContent>
          <MDBox mt={1} display="grid" gap={2}>
            <MDTypography variant="button" color="text">
              Move <strong>{getListingTitleLabel(moveListingItem)}</strong> to:
            </MDTypography>
            <TextField
              select
              fullWidth
              label="Target Category"
              value={moveTargetCategoryId}
              onChange={(e) => setMoveTargetCategoryId(Number(e.target.value))}
            >
              {categories
                .filter((category) => getId(category) !== manageCategoryId)
                .map((category) => (
                  <MenuItem key={`move-cat-${getId(category)}`} value={getId(category)}>
                    {getName(category)}
                  </MenuItem>
                ))}
            </TextField>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseMoveListing}
            color="secondary"
            disabled={listingStatusLoadingId !== null}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmMoveListing}
            variant="gradient"
            color="info"
            disabled={listingStatusLoadingId !== null}
          >
            Move
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Categories;
